import React from 'react';
import {Button, Drawer, InputNumber, Select, Slider} from 'antd';

import {messageType, showMessages} from '../../helpers/message';
import {nothingToUpdate} from '../../helpers/text';
import {userHasRole} from '../../lib/role';

import {useGetCarRateClassesQuery} from '../../store/api/optionsApi';
import {useDiscountBookingMutation} from '../../store/api/bookingsApi';
import {BaseError} from '../../types/apiTypes';
import {BookingDiscountProps} from '../../types/bookingTypes';

import './BookingDiscount.less';

const BookingDiscount = ({bookingDiscounts, rateClass, bookingID}: BookingDiscountProps) => {
    const mobileMode = window.innerWidth <= 768;
    const isMCT = userHasRole('staff', 'developer') || userHasRole('staff', 'member-care');

    const {data: rateClassOptions, isLoading: rateClassesLoading} = useGetCarRateClassesQuery();
    const [discountBooking, {isLoading: discountLoading, isSuccess: discountSuccess, isError: isDiscountError, error: discountError, reset: discountReset}] = useDiscountBookingMutation();
    // We will use another instance of the mutation for the rate class to differentiate the success messages.
    const [rateClassUpdate, {isLoading: rateClassUpdateLoading, isSuccess: rateClassUpdateSuccess, isError: isRateClassUpdateError, error: rateClassUpdateError, reset: rateClassUpdateReset}] = useDiscountBookingMutation();

    const existingManualDiscount = bookingDiscounts.find(cost => cost.type === 'manual');
    const manualDiscount = existingManualDiscount ? existingManualDiscount.percent * 100 : 0;

    const existingRateClassDiscount = bookingDiscounts.find(cost => cost.type === 'rate_class');
    const rateClassDiscount = existingRateClassDiscount ? existingRateClassDiscount['portion'] : rateClass;

    const [open, setOpen] = React.useState(false);
    const [sliderValue, setSliderValue] = React.useState<number | null>(manualDiscount);
    const [newRateClass, setNewRateClass] = React.useState(rateClassDiscount);

    React.useEffect(() => {
        if (discountSuccess) {
            showMessages('Discount applied.', messageType.success);
            discountReset();
        } else if (isDiscountError) {
            showMessages((discountError as BaseError).data?.message, messageType.error);
            discountReset();
        } else if (rateClassUpdateSuccess) {
            showMessages('Rate class change has been successfully applied. Your cost may have been discounted.', messageType.success);
            rateClassUpdateReset();
        } else if (isRateClassUpdateError) {
            showMessages((rateClassUpdateError as BaseError).data?.message, messageType.error);
            rateClassUpdateReset();
        }
    }, [discountError, discountReset, discountSuccess, isDiscountError, isRateClassUpdateError, rateClassUpdateError, rateClassUpdateReset, rateClassUpdateSuccess]);

    const onOpen = () => {
        setOpen(true);
        setSliderValue(manualDiscount);
    }

    const onClose = () => {
        setOpen(false);
        setSliderValue(manualDiscount);
    }

    const onFinish = () => {
        const discountBodies = [];

        // Add manual discount if applied.
        if (manualDiscount !== sliderValue) {
            discountBodies.push({
                bookingID,
                portion: 'all',
                type: 'manual',
                percent: sliderValue ? sliderValue / 100 : 0,
                amount: null,
            });
        }

        // Add rate class discount if applied.
        if (rateClassDiscount !== newRateClass) {
            // Unshift to the beginning of the array so rate class discounts happen first.
            discountBodies.unshift({
                bookingID,
                portion: newRateClass,
                type: 'rate_class',
                percent: null,
                amount: null,
            });
        }

        if (!discountBodies.length) {
            // If no change, nothing to update.
            showMessages(nothingToUpdate, messageType.error);
            return;
        }

        for (let discount of discountBodies) {
            if (discount.type === 'rate_class') {
                rateClassUpdate(discount);
            } else {
                discountBooking(discount);
            }
        }
        onClose();
    }

    const sliderMarks = {
        0: 0,
        25: 25,
        50: 50,
        75: 75,
        100: 100
    }

    return (
        <>
            {
            isMCT ?
                <Button onClick={onOpen}>
                    Manage Discounts
                </Button>
            : null
            }
            <Drawer
                title="Manage Discounts"
                className="drawer-wrapper"
                width={mobileMode ? "100%" : "55%"}
                open={open}
                onClose={onClose}
            >
                <label className="drawer-percent-label">Discount Percentage:</label>
                <div className="slider-input-container">
                    <Slider
                        value={sliderValue ?? 0}
                        marks={sliderMarks}
                        onChange={value => setSliderValue(value)}
                        className="slider"
                        min={0}
                        max={100}
                        tooltip={{
                            // Tooltip is throwing strange error, so this just removes tooltip from slider.
                            formatter: null
                        }}
                    />
                    <InputNumber
                        value={sliderValue ?? 0}
                        onChange={value => setSliderValue(value)}
                        addonAfter="%"
                        className="slider-input-number"
                        min={0}
                        max={100}
                    />
                </div>
                <label className="drawer-rate-class-label">Rate Class:</label>
                <Select
                    disabled={rateClassesLoading || !rateClassOptions?.length}
                    onChange={value => setNewRateClass(value)}
                    className="rate-class-dropdown"
                    defaultValue={rateClassDiscount || rateClass || 'Daily Drive'}
                    options={rateClassOptions?.map(rateClass => {
                        return {
                            key: rateClass.id,
                            label: rateClass.name,
                            value: rateClass.name,
                        }
                    })}
                >
                </Select>
                <div className="button-container">
                    <Button onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onFinish}
                        loading={discountLoading || rateClassUpdateLoading}
                        type="primary"
                        htmlType="submit"
                        className="apply-button"
                    >
                        Apply
                    </Button>
                </div>
            </Drawer>
        </>
    )
}

export default BookingDiscount;
